/**
 * THE END OF A TURN, decided by WORDS, not by quiet. An energy VAD ends a turn only after ~700 ms of silence, and a salon
 * with music never has that: the state stayed on "Hearing you" for ever (iOS simulator, 26 Oct, a pulsing test tone). So
 * once the energy has started a turn, it is the live words that end it:
 *   - words have been heard and no NEW word (a live delta) for ~1.0 s: commit. ~0.7 s when the words so far end a sentence
 *     (. ? ! ؟ …), ~1.4 s when they end mid-clause (a comma, or a connective: and / ו / و ...);
 *   - a hard cap: 20 s of continuous hearing is committed;
 *   - no words at all 2.5 s after the energy rose: it was noise. Back to listening; nothing is said or sent.
 * The clock is the microphone's own (the sum of frame lengths), so it needs no timers and is deterministic in tests.
 */
export const HARD_CAP_MS = 20_000;
export const NO_WORDS_MS = 2_500;
export const BREATH_MS = 250; // the sound must have been quiet this long before WORDS may end a turn (the live words lag the voice by ~0.8 s)
export const STEADY_SOUND_EXTRA_MS = 1_200; // ...unless the sound never goes quiet (music): then the words rule waits this much longer
const SENTENCE_END = /[.?!؟…]["'”’)\]]*\s*$/;
const MID_CLAUSE_PUNCT = /[,;:،؛\-–—]\s*$/;
// Words a sentence cannot end on: the person has more to say. Whole words only (he/ar prefixes cannot be told from a word).
const CONNECTIVES = new Set([
  'and', 'but', 'or', 'so', 'because', 'with', 'to', 'the', 'a', 'an', 'for', 'if', 'that', 'of', 'at', 'on', 'in', 'then', 'also', 'my', 'her', 'his',
  'ו', 'אבל', 'או', 'כי', 'עם', 'של', 'אם', 'ש', 'את', 'על', 'ב', 'ל', 'גם', 'אז', 'כדי', 'לפני', 'אחרי', 'בין',
  'و', 'لكن', 'أو', 'او', 'بس', 'مع', 'لأن', 'لان', 'إذا', 'اذا', 'ثم', 'كمان', 'على', 'في', 'من', 'إلى', 'الى', 'عن', 'يعني', 'بعدين',
]);

/** How long the last word's silence must last before the turn is over, for what has been heard so far. */
export function turnEndWindowMs(text) {
  const t = String(text || '').trim();
  if (SENTENCE_END.test(t)) return 700;
  if (MID_CLAUSE_PUNCT.test(t)) return 1400;
  const last = t.split(/\s+/).pop().toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  return CONNECTIVES.has(last) ? 1400 : 1000;
}

export function createTurnClock() {
  let active = false;
  let elapsed = 0;
  let lastDeltaAt = 0;
  let text = '';
  return {
    start() { active = true; elapsed = 0; lastDeltaAt = 0; text = ''; },
    reset() { active = false; elapsed = 0; lastDeltaAt = 0; text = ''; },
    /** A live delta arrived: `all` is every word heard so far this turn. */
    delta(all) { if (!active) return; text = String(all || ''); if (text.trim()) lastDeltaAt = elapsed; },
    /** One microphone frame of `dtMs`: 'commit' | 'discard' | null. `quietMs` is how long the sound has been quiet (omit it and only the words decide). */
    tick(dtMs, quietMs) {
      if (!active) return null;
      elapsed += dtMs;
      const heard = text.trim().length > 0;
      if (elapsed >= HARD_CAP_MS) return heard ? 'commit' : 'discard';
      if (!heard) return elapsed >= NO_WORDS_MS ? 'discard' : null;
      const still = elapsed - lastDeltaAt;
      const window = turnEndWindowMs(text);
      if (still < window) return null;
      // The words are behind the voice: a full stop that came early and a model that took 0.8 s for the next words must not end a turn the person is still speaking.
      if (quietMs != null && quietMs < BREATH_MS && still < window + STEADY_SOUND_EXTRA_MS) return null;
      return 'commit';
    },
    get active() { return active; },
  };
}
